import type {
	PbLineItemPackage,
	PbPackageProduct,
	ProducerNumber,
} from '@/apiTypes'
import { Disposition } from '@/enums/Disposition'
import type { DocumentType } from '@/enums/DocumentType'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { PlanEverplansPaymentType } from '@/enums/PlanEverplansPaymentType'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { PlanPaymentType } from '@/enums/PlanPaymentType'
import { ViewingOption } from '@/enums/ViewingOption'
import { isCartSubItem } from '@/guards'
import { getBlankEft } from '@/mixins/eft'
import { deepToRaw } from '@/services/deepToRaw'
import {
	planV2EftMigration,
	type v1PlanData,
} from '@/services/migrations/planV2EftMigration'
import type { EncryptedData, EncryptionStatus } from '@/stores/encryption'
import type {
	CartItem,
	CartSubItem,
	CustomCartItem,
	EFTPayment,
	EverplansPayment,
	InsuranceDetails,
	InsuranceScenarioOutput,
	OapPayment,
	PlanBasicData,
	PlanData,
	PlanSensitiveData,
} from '@/types'
import { faker } from '@faker-js/faker'
import { Frequency, PaymentType } from '@precoa/calculator-sdk'

export enum PlanSelectionDataName {
	visitation = 'visitationLocationSelection',
}

export enum PlanErrorType {
	PolicyEmail = 'policyEmail',
	EverplansEmail = 'everplansEmail',
}

export type IndexedPlan = {
	index: number
	plan: Plan
}

export type SplitPlan = {
	basic: Partial<PlanBasicData> & Pick<Plan, 'id'>
	sensitive: PlanSensitiveData
	vault: EncryptedData | null
}

export default class Plan implements PlanData {
	id = ''

	leadId: string | null = ''
	contactId: string | null = ''
	opportunityId: string | null = ''
	funeralHomeId = ''
	// Vital Info
	firstName = ''
	middleName = ''
	lastName = ''
	maidenName = ''
	address = ''
	city = ''
	state = ''
	zip = ''
	email = ''
	phone = ''
	secondaryPhone = ''
	socialSecurityNumber = ''
	birthDate: string | undefined = undefined
	birthCity = ''
	birthState = ''
	birthCounty = ''
	birthCountry = 'US'
	educationLevel = ''
	occupation = ''
	occupationYears = ''
	businessType = ''
	race = ''
	gender = ''
	maritalStatus = ''
	vitalInfoNotes = ''
	ownerIsInsured = true
	beneficiary = ''
	beneficiaryRelationship = ''

	policyOwner: PlanData['policyOwner'] = {
		firstName: '',
		middleName: '',
		lastName: '',
		birthDate: undefined,
		socialSecurityNumber: '',
		address: '',
		city: '',
		state: '',
		zip: '',
		email: '',
		phone: '',
		secondaryPhone: '',
	}
	completedSteps = ['']
	paymentStep: 'html' | 'document' = 'html'
	oapStep: 'html' | 'document' = 'html'
	everplansStep: 'html' | 'document' = 'html'
	packetStatus = PlanPacketStatus.Incomplete

	insuranceProducerNumber?: ProducerNumber
	oapProducerNumber?: ProducerNumber

	payment: PlanData['payment'] = Plan.getDefaultPayment()

	oapPayment: OapPayment = {
		paymentPlanId: null,
		provider: null,
		paymentPlan: null,
	}

	everplansPayment: EverplansPayment = {
		paymentType: PlanEverplansPaymentType.EFT,
		paymentDetails: getBlankEft(),
		amount: 0,
	}

	// Spouse Info
	spouseInfo = {
		firstName: '',
		middleName: '',
		lastName: '',
		maidenName: '',
		marriageDate: undefined,
		marriageCity: '',
		marriageState: '',
		marriageCounty: '',
	}

	// Parental Info
	fatherInfo = {
		firstName: '',
		middleName: '',
		lastName: '',
		city: '',
		state: '',
		birthCounty: '',
		birthCountry: 'US',
	}
	motherInfo = {
		firstName: '',
		middleName: '',
		maidenName: '',
		city: '',
		state: '',
		birthCounty: '',
		birthCountry: 'US',
	}
	parentalInfoNotes = ''
	militaryRecordNotes = ''
	militaryRecord = []
	militaryRequests = []
	peopleResponsible = [
		{
			firstName: '',
			lastName: '',
			relationship: '',
			city: '',
			state: '',
			zip: '',
			phone: '',
			email: '',
			notes: '',
		},
	]

	// Service Preferences
	funeralHomeName = ''
	placeOfService = ''
	placeOfGathering = ''
	religiousPreference = ''
	serviceOrganization = ''
	serviceNotes = ''

	burialOrCremation: Disposition | null = null
	intermentPrefs = ''
	cemeteryPurchaseStatus = ''
	cemeteryPropertyOwner = ''
	cemeteryName = ''
	intermentCity = ''
	intermentState = ''
	intermentNotes = ''
	postServiceMeal = false
	postServiceMealDetails = ''
	viewingPreference = ViewingOption.None
	specialInstructions = {
		flowerType: '',
		colorPreferences: '',
		musicPreference: '',
		casketBearers: '',
		wantsToNotifyBearers: false,
		obitPublications: '',
		wantsObitPhoto: false,
	}
	accessoriesInstructions = [
		{
			item: '',
			returnTo: '',
		},
	]
	otherMemorialNotes = ''

	mostImportantReason = ''

	visitationLocationSelection = ''
	oapIncluded = false
	everplansIncluded = false
	everplansEmail?: string
	cartItems: CartItem[] = []
	customCartItems: CustomCartItem[] = []
	lastUpdated: string | undefined = undefined

	// Payment settings
	insurance: InsuranceDetails = {
		paymentType: PaymentType.MP_EFT,
		terminallyIll: false,
		otherIllness: false,
		paymentFrequency: Frequency.Monthly,
	}

	insuranceScenarioOutput: InsuranceScenarioOutput = {
		planTerm: undefined,
		totalCost: 0,
		paidToday: 0,
		firstPayment: 0,
		downPayment: 0,
		paymentSummary: [],
	}

	vault: EncryptedData | null = null
	encryptionStatus: EncryptionStatus | undefined = undefined

	documents: {
		id: string
		type: DocumentType
	}[] = []

	errors: { type: PlanErrorType; title: string; message: string }[] = []

	constructor(plan: Partial<PlanData> | undefined = undefined) {
		Object.assign(this, structuredClone(deepToRaw(plan)))

		if (!this.id) {
			this.id = faker.string.uuid()
		}

		// Sometimes the id of plan from CloudSync come back as uppercase so we need to turn it to lowercase
		this.id = this.id.toLowerCase()
	}

	public serialize(): SplitPlan {
		return Plan.splitPlan(this)
	}

	public update(data: Partial<PlanData>) {
		Object.assign(this, data)
	}

	public static deserialize(
		version: number | undefined = undefined,
		data: Partial<PlanData>,
		vault: EncryptedData | null = null
	): Plan {
		if (version === 1) {
			data = planV2EftMigration(data as Partial<v1PlanData>)
		}

		const plan = new Plan(data)
		plan.vault = vault
		return plan
	}

	public getInitials(): string {
		return this.firstName.substring(0, 1) + this.lastName.substring(0, 1)
	}

	/*** Shopping Cart Methods for updating plan data. Should not rely on Vue or Pinia. ***/
	/** Utility to add a line item to the shopping cart */
	public addCartProduct(
		lineItemId: string,
		quantity: number = 1,
		price: number | null = null
	) {
		if (quantity <= 0) {
			return this.hardDeleteCartItem(lineItemId)
		}

		const match = this.cartItems.find((i) => i.lineItemId === lineItemId)

		if (match) {
			match.isSoftDeleted = false
			match.quantity = Math.max(1, quantity)
			match.price = price
			return
		}
		this.cartItems.push({
			quantity,
			lineItemId,
			price,
			isSoftDeleted: false,
			subItems: [],
		})
	}

	/** Add a package to a cart */
	public addCartPackage(pkg: PbLineItemPackage, subItems: PbPackageProduct[]) {
		const match = this.cartItems.find((i) => i.lineItemId === pkg.id)

		const cartSubItems: CartSubItem[] = subItems.map((item) => ({
			packageLineItemId: pkg.id,
			lineItemId: item.refSpbLineProduct,
			isSoftDeleted: false,
			price: item.fixedPrice,
			quantity: undefined,
		}))

		if (match) {
			match.subItems = cartSubItems
			match.isSoftDeleted = false
			return
		}
		this.cartItems.push({
			lineItemId: pkg.id,
			quantity: 1,
			price: null,
			isSoftDeleted: false,
			subItems: cartSubItems,
		})

		this.onPackageAdd(pkg)
	}

	/** Update plan based on the presence of the given package */
	public onPackageAdd(pkg: PbLineItemPackage) {
		if (pkg.subcategory === PbSubcategory.PackageBurial) {
			this.burialOrCremation = Disposition.Burial
		} else if (pkg.subcategory === PbSubcategory.Cremation) {
			this.burialOrCremation = Disposition.Cremation
		}
	}

	public deleteSubItem(lineItemId: string, subItemId: string) {
		const matchItemIndex = this.cartItems.findIndex(
			(i) => i.lineItemId === lineItemId
		)

		if (matchItemIndex === -1) {
			throw new Error('Line item not found; cannot soft remove')
		}

		const matchSubItemIndex = this.cartItems[matchItemIndex].subItems.findIndex(
			(i) => i.lineItemId === subItemId
		)

		if (matchSubItemIndex === -1) {
			throw new Error('Line item not found; cannot soft remove')
		}

		this.cartItems[matchItemIndex].subItems.splice(matchSubItemIndex, 1)
	}

	/** Mark or unmark an item as soft deleted */
	public softDeleteCartItem(lineItemId: string, isSoftDeleted: boolean = true) {
		const match = this.cartItems.find((i) => i.lineItemId === lineItemId)

		if (!match) {
			throw new Error('Line item not found; cannot soft remove')
		}

		match.isSoftDeleted = isSoftDeleted
	}

	/** Remove an item or sub-item from the cart */
	public delete(cartItem: CartSubItem | CartItem) {
		if (isCartSubItem(cartItem)) {
			this.deleteSubItem(cartItem.packageLineItemId, cartItem.lineItemId)
		} else {
			this.hardDeleteCartItem(cartItem.lineItemId)
		}
	}

	/** Remove a cart item */
	public hardDeleteCartItem(lineItemId: string) {
		this.cartItems = this.cartItems.filter((i) => i.lineItemId !== lineItemId)
	}

	/** Add a custom item to the plan */
	public addCustomItem(item: Omit<CustomCartItem, 'id' | 'isSoftDeleted'>) {
		// Generate an id 1 higher than the current max ID
		const id =
			1 +
			this.customCartItems.reduce(
				(maxId, item) => Math.max(maxId, item.id ?? 0),
				0
			)

		this.customCartItems.push({
			...item,
			id,
			isSoftDeleted: false,
		} as CustomCartItem)

		return id
	}

	/** Mark or unmark an item as soft deleted */
	public softDeleteCustomItem(id: number, isSoftDeleted: boolean = true) {
		const match = this.customCartItems.find((i) => i.id === id)

		if (!match) {
			throw new Error('Custom cart item not found; cannot soft remove')
		}

		match.isSoftDeleted = isSoftDeleted
	}

	/** Remove a custom item */
	public hardDeleteCustomItem(itemId: number) {
		this.customCartItems = this.customCartItems.filter((i) => i.id !== itemId)
	}

	/** Toggle a package on this plan, replacing any existing one if needed. Also manages OAP status upon selection. */
	public togglePackage(
		pkg: PbLineItemPackage,
		subItems: PbPackageProduct[],
		allPackages: PbLineItemPackage[]
	) {
		const essentialsIds = new Set(
			allPackages
				.filter((p) => p.subcategory === PbSubcategory.PackageEssentials)
				.map((p) => p.id)
		)
		const nonEssentialsIds = new Set(
			allPackages
				.filter((p) => p.subcategory !== PbSubcategory.PackageEssentials)
				.map((p) => p.id)
		)
		const allPackageIds = new Set([...essentialsIds, ...nonEssentialsIds])

		const isEssentials = essentialsIds.has(pkg.id)
		const hasThisPackage = this.cartItems.some(
			(cartItem) => cartItem.lineItemId === pkg.id
		)

		// REMOVE this package if already selected
		if (hasThisPackage) {
			this.hardDeleteCartItem(pkg.id)

			// Finds a package that is still in the cart that either
			// is an essentials package or has no cost everplans
			const stillGrantsEverplans = this.cartItems.some((cartItem) => {
				const remainingPkg = allPackages.find(
					(p) => p.id === cartItem.lineItemId
				)

				return (
					remainingPkg?.subcategory === PbSubcategory.PackageEssentials ||
					!!remainingPkg?.isNoCostEverplans
				)
			})

			// If the package being removed is an essentials package or has no cost everplans,
			// and there are no other packages that should grant everplans,
			// then we need to turn off everplans
			if ((isEssentials || pkg.isNoCostEverplans) && !stillGrantsEverplans) {
				this.everplansIncluded = false
			}

			// Clear OAP only if no packages remain after removal
			const stillHasAnyPackage = this.cartItems.some((cartItem) =>
				allPackageIds.has(cartItem.lineItemId)
			)
			if (!stillHasAnyPackage) {
				this.oapIncluded = false
			}

			return
		}

		// ADDING a package
		if (isEssentials) {
			// If adding an essentials package, remove existing essentials package first (enforce 1 essentials)
			this.cartItems
				.filter(
					(cartItem) =>
						cartItem.lineItemId !== pkg.id &&
						essentialsIds.has(cartItem.lineItemId)
				)
				.forEach((cartItem) => this.hardDeleteCartItem(cartItem.lineItemId))
		} else {
			// If adding a non-essentials package, remove existing non-essentials packages first (enforce 1 non-essentials)
			this.cartItems
				.filter((cartItem) => nonEssentialsIds.has(cartItem.lineItemId))
				.forEach((cartItem) => this.hardDeleteCartItem(cartItem.lineItemId))

			// Also remove essentials packages if any exist
			this.cartItems
				.filter((cartItem) => essentialsIds.has(cartItem.lineItemId))
				.forEach((cartItem) => this.hardDeleteCartItem(cartItem.lineItemId))

			// Turn off Everplans since essentials packages are gone
			this.everplansIncluded = false
		}

		// Add selected package
		this.addCartPackage(pkg, subItems)

		// Essentials auto-includes Everplans at paid price; isNoCostEverplans grants free
		// Everplans (UI/submit treat as no-cost). Essentials alone is not free.
		if (isEssentials || pkg.isNoCostEverplans) {
			this.everplansIncluded = true
		}

		// Enable OAP only for non-essentials
		if (!isEssentials) {
			this.oapIncluded = true
		}
	}

	/** Split a plan into basic and sensitive data, plus the encrypted vault */
	public static splitPlan(planData: Plan): SplitPlan {
		const {
			id,
			funeralHomeId,
			leadId,
			contactId,
			opportunityId,
			firstName,
			middleName,
			lastName,
			address,
			city,
			state,
			zip,
			email,
			phone,
			secondaryPhone,
			completedSteps,
			paymentStep,
			oapStep,
			everplansStep,
			cartItems,
			oapIncluded,
			everplansIncluded,
			customCartItems,
			lastUpdated,
			vault,
			encryptionStatus,
			insuranceProducerNumber,
			oapProducerNumber,
			documents,
			...sensitiveData
		} = planData

		const basic: Partial<PlanBasicData> & Pick<PlanData, 'id'> = {
			id,
			funeralHomeId,
			leadId,
			contactId,
			opportunityId,
			firstName,
			middleName,
			lastName,
			address,
			city,
			state,
			zip,
			email,
			phone,
			secondaryPhone,
			completedSteps,
			paymentStep,
			oapStep,
			everplansStep,
			cartItems,
			oapIncluded,
			everplansIncluded,
			customCartItems,
			lastUpdated,
			insuranceProducerNumber,
			oapProducerNumber,
			documents,
		}

		return { basic, sensitive: sensitiveData as PlanSensitiveData, vault }
	}

	public hasBurialPackage(currentPricebookPackages: PbLineItemPackage[]) {
		const burialPackageIds = new Set(
			currentPricebookPackages
				.filter((p) => p.subcategory === PbSubcategory.PackageBurial)
				.map((p) => p.id)
		)

		return this.cartItems.some((cartItem) =>
			burialPackageIds.has(cartItem.lineItemId)
		)
	}

	public static getDefaultPayment(): EFTPayment {
		return {
			paymentType: PlanPaymentType.EFT,
			paymentDetails: {
				initialAccount: getBlankEft(),
				ongoingAccount: getBlankEft(),
				useDifferentOngoingAccount: false,
			},
		}
	}

	public isEverplansPremium(): boolean {
		return this.everplansIncluded
	}
}
